import { useEffect, useMemo, useRef, useState } from 'react';
import { TEMPLATES } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { COMMANDS, formatKeys } from '../commands.ts';
import { installedPlugins, runPlugin, type PluginDef } from '../plugins.ts';
import { insertWidget, SAMPLE_WIDGETS } from '../widgets.ts';
import { insertTemplateById } from './Templates.tsx';
import { pluginParameters, type ParamPrompt } from '../pluginParams.ts';

interface Action {
  kind: 'Command' | 'Plugin' | 'Widget' | 'Template' | 'Component' | 'Layer';
  label: string;
  hint?: string;
  run: () => void;
}

/** Score how well `q` matches `s`: words in order, earlier and tighter is better (0 = no match). */
function score(s: string, q: string): number {
  if (!q) return 1;
  const t = s.toLowerCase();
  let i = 0,
    total = 0;
  for (const word of q.toLowerCase().split(/\s+/).filter(Boolean)) {
    const at = t.indexOf(word, i);
    if (at < 0) return 0;
    total += 100 - Math.min(90, at) + (at === 0 || t[at - 1] === ' ' ? 30 : 0);
    i = at + word.length;
  }
  return total;
}

const isWidget = (code: string) => /widget\.register\s*\(/.test(code);

/** Quick actions (⌘K / ⌘/): find any command, plugin, widget, template, component or layer. */
export function QuickActions() {
  const open = useApp('ui', () => app.ui.quickActionsOpen);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [prompt, setPrompt] = useState<ParamPrompt | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setPrompt(null);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  const close = () => app.setUI({ quickActionsOpen: false });
  const runPluginAction = (p: PluginDef) => {
    if (p.parameters?.length) {
      // plugins with parameters ask for them here first
      setQ('');
      setPrompt(pluginParameters(p, () => close(), setPrompt));
      return;
    }
    close();
    if (isWidget(p.code)) insertWidget(p.name, p.code);
    else runPlugin(p);
  };
  const actions = useMemo<Action[]>(() => {
    if (!open) return [];
    const doc = app.doc;
    const kind = app.pageKind() === 'WHITEBOARD' ? 'WHITEBOARD' : 'DESIGN';
    const out: Action[] = [];
    for (const c of COMMANDS) if (!c.enabled || c.enabled()) out.push({ kind: 'Command', label: c.label, hint: c.keys?.length ? formatKeys(c.keys) : undefined, run: () => (close(), c.run()) });
    for (const p of installedPlugins()) out.push({ kind: isWidget(p.code) ? 'Widget' : 'Plugin', label: p.name, run: () => runPluginAction(p) });
    for (const w of SAMPLE_WIDGETS) out.push({ kind: 'Widget', label: w.name, run: () => (close(), insertWidget(w.name, w.code)) });
    for (const t of TEMPLATES) out.push({ kind: 'Template', label: t.name, hint: t.kind === kind ? undefined : t.kind.toLowerCase(), run: () => (close(), insertTemplateById(t)) });
    for (const n of doc.nodes.values()) {
      if (n.type === 'COMPONENT' && !doc.get(n.parent)?.isComponentSet)
        out.push({
          kind: 'Component',
          label: n.name,
          run: () => {
            close();
            const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
            const id = app.editor.createInstance(n.id, { parent: app.editor.pageId, x: Math.round(c.x - n.width / 2), y: Math.round(c.y - n.height / 2) });
            app.editor.setSelection([id]);
          },
        });
    }
    doc.walk(app.editor.pageId, (n) => {
      if (n.id === app.editor.pageId) return;
      out.push({ kind: 'Layer', label: n.name, hint: n.type.toLowerCase(), run: () => (close(), app.editor.setSelection([n.id]), app.zoomToNodes([n.id])) });
      if (n.type === 'INSTANCE') return false;
    });
    return out;
  }, [open]);
  if (!open) return null;
  const order = ['Command', 'Plugin', 'Widget', 'Template', 'Component', 'Layer'];
  const shown = prompt
    ? prompt.suggestions.filter((s) => score(s.name, q) > 0).map((s): Action => ({ kind: 'Plugin', label: s.name, run: () => prompt.choose(s, q, setPrompt) }))
    : actions
        .map((a) => ({ a, s: score(a.label, q) - (a.kind === 'Layer' ? 40 : 0) }))
        .filter((x) => x.s > 0)
        .sort((x, y) => y.s - x.s || order.indexOf(x.a.kind) - order.indexOf(y.a.kind))
        .slice(0, 50)
        .map((x) => x.a);
  const go = (i: number) => {
    if (prompt && !shown.length && prompt.freeform) return prompt.choose({ name: q, data: q }, q, setPrompt);
    shown[i]?.run();
  };
  return (
    <div className="modal-backdrop quick-backdrop" onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="quick-actions">
        {prompt && (
          <div className="quick-param">
            <b>{prompt.plugin}</b> · {prompt.current.name}
            {prompt.current.description && <span> — {prompt.current.description}</span>}
          </div>
        )}
        <input
          ref={input}
          value={q}
          placeholder={prompt ? (prompt.message ?? `Choose ${prompt.current.name.toLowerCase()}…`) : 'Search commands, plugins, templates, components and layers'}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
            prompt?.query(e.target.value, setPrompt);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowDown') (e.preventDefault(), setActive((a) => Math.min(shown.length - 1, a + 1)));
            if (e.key === 'ArrowUp') (e.preventDefault(), setActive((a) => Math.max(0, a - 1)));
            if (e.key === 'Enter') (e.preventDefault(), go(active));
          }}
        />
        <div className="quick-list">
          {shown.map((a, i) => (
            <button key={`${a.kind}-${a.label}-${i}`} className={i === active ? 'on' : ''} onMouseEnter={() => setActive(i)} onClick={() => go(i)}>
              <span className="quick-kind">{prompt ? '' : a.kind}</span>
              <span className="quick-label">{a.label}</span>
              {a.hint && <span className="quick-hint">{a.hint}</span>}
            </button>
          ))}
          {!shown.length && <div className="quick-empty">{prompt?.freeform ? 'Press Enter to use what you typed' : 'Nothing matches'}</div>}
        </div>
      </div>
    </div>
  );
}
